import type { ComponentType } from 'react'
import { parseFrontmatter, type DocFrontmatter } from './frontmatter'

export interface DocEntry extends DocFrontmatter {
  /** Path under content/docs without extension, e.g. 'getting-started/what-is-a-persona'. */
  slug: string
  /** In-app route, e.g. '/help/getting-started/what-is-a-persona'. */
  route: string
  /** The compiled MDX component. */
  Component: ComponentType
}

type MdxModule = { default: ComponentType; frontmatter?: unknown }

// Eagerly import every doc so the registry is available synchronously.
// (When the corpus grows, switch to lazy glob + a generated index; see CONTRIBUTING-DOCS.md.)
const modules = import.meta.glob<MdxModule>('./**/*.mdx', { eager: true })

function toSlug(globPath: string): string {
  return globPath.replace(/^\.\//, '').replace(/\.mdx$/, '')
}

export const docs: DocEntry[] = Object.entries(modules)
  .map(([globPath, mod]) => {
    const slug = toSlug(globPath)
    let fm: DocFrontmatter
    try {
      fm = parseFrontmatter(mod.frontmatter)
    } catch (err) {
      throw new Error(`Invalid frontmatter in content/docs/${slug}.mdx: ${String(err)}`)
    }
    return { ...fm, slug, route: `/help/${slug}`, Component: mod.default }
  })
  .sort((a, b) => a.order - b.order)

export function getDoc(slug: string): DocEntry | undefined {
  return docs.find((d) => d.slug === slug)
}

/**
 * Filter docs for a rendering surface.
 * - `internal` is never returned.
 * - `authenticated`-tier docs require `authenticated: true`.
 * - a page with a `feature` gate requires `isEnabled(feature)`.
 */
export function visibleDocs(opts: {
  authenticated: boolean
  isEnabled: (feature: string) => boolean
}): DocEntry[] {
  return docs.filter((d) => {
    if (d.visibility === 'internal') return false
    if (d.visibility === 'authenticated' && !opts.authenticated) return false
    if (d.feature && !opts.isEnabled(d.feature)) return false
    return true
  })
}
